import { AdvertiserModalMode } from '@/Enums/Clients/AdvertiserModalMode.enum';
import {
  IAdvertiserInClientFormData,
  IAgencyInClientFormData,
  IClientAdvertiserPayload,
  IClientAgencyPayload,
  IClientFormData,
} from '@/Interfaces/Client.interface';
import { IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { DEFAULT_ADVERTISER } from '@/constants/advertisers';
import { DEFAULT_AGENCY } from '@/constants/agencies';
import { DEFAULT_CLIENT } from '@/constants/clients';
import { formatTemporaryControlOption } from '@/helpers/clients';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectClientsControlOptions,
  selectClientsData,
  selectSelectedClient,
} from '@/store/selectors/clients.selectors';
import { getAdvertiserCategories } from '@/store/slices/advertisers.slice';
import {
  clearSelectedClient,
  getAdvertisers,
  getAgencies,
  getClients,
  resetClientsPageState,
} from '@/store/slices/clients.slice';
import { getWhitelabels } from '@/store/slices/sharedApi.slice';
import { Card } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import AddAdvertiserModal from './AddAdvertiserModal';
import AddAgencyModal from './AddAgencyModal';
import ClientModal from './ClientModal';
import ClientsGrid from './ClientsGrid';

const Clients = () => {
  const dispatch = useAppDispatch();

  const { data, loading: dataLoading, filters, total } = useAppSelector(selectClientsData);
  const { loading: controlOptionsLoading } = useAppSelector(selectClientsControlOptions);

  const [loading, setLoading] = useState<boolean>(false);
  const [openClientModal, setOpenClientModal] = useState<boolean>(false);
  const [openAdvertiserModal, setOpenAdvertiserModal] = useState<boolean>(false);
  const [openAgencyModal, setOpenAgencyModal] = useState<boolean>(false);
  const [advertiserModalMode, setAdvertiserModalMode] = useState<AdvertiserModalMode | null>(null);

  const isLoading = useMemo(
    () => dataLoading || loading || controlOptionsLoading,
    [dataLoading, loading, controlOptionsLoading],
  );

  const selectedClient = useAppSelector(selectSelectedClient);

  //#region Client Form
  const clientForm = useForm<IClientFormData>({
    defaultValues: selectedClient
      ? {
          ...selectedClient,
          newAgencies: [],
          newAdvertisers: [],
        }
      : DEFAULT_CLIENT,
  });

  const { control: clientFormControl, setValue: setClientFormValue } = clientForm;

  const clientName: string = useWatch({ control: clientFormControl, name: 'name' });
  const selectedWhitelabel: IControlOption | null = useWatch({ control: clientFormControl, name: 'whitelabel' });
  const selectedAdvertisersInClient: IControlOption[] = useWatch({ control: clientFormControl, name: 'advertisers' });
  const selectedNewAdvertisersInClient: IClientAdvertiserPayload[] = useWatch({
    control: clientFormControl,
    name: 'newAdvertisers',
  });
  const selectedAgenciesInClient: IControlOption[] = useWatch({ control: clientFormControl, name: 'agencies' });
  const selectedNewAgenciesInClient: IClientAgencyPayload[] = useWatch({
    control: clientFormControl,
    name: 'newAgencies',
  });
  //#endregion Client Form

  //#region Advertiser Form
  const advertiserForm = useForm<IAdvertiserInClientFormData>({
    defaultValues: { ...DEFAULT_ADVERTISER, client: clientName },
  });

  const { reset: resetAdvertiserForm } = advertiserForm;
  //#endregion Advertiser Form

  //#region Agency Form
  const agencyForm = useForm<IAgencyInClientFormData>({
    defaultValues: {
      ...DEFAULT_AGENCY,
      client: clientName,
      newAdvertisers: [],
    },
  });

  const { control: agencyFormControl, reset: resetAgencyForm, setValue: setAgencyFormValue } = agencyForm;

  const selectedAdvertisersInAgency: IControlOption[] = useWatch({ control: agencyFormControl, name: 'advertisers' });
  const selectedNewAdvertisersInAgency: IClientAdvertiserPayload[] = useWatch({
    control: agencyFormControl,
    name: 'newAdvertisers',
  });
  //#endregion Agency Form

  useEffect(() => {
    resetAdvertiserForm({ ...DEFAULT_ADVERTISER, client: clientName });
    resetAgencyForm({
      ...DEFAULT_AGENCY,
      client: clientName,
      newAdvertisers: [],
    });
  }, [clientName, resetAdvertiserForm, resetAgencyForm]);

  useEffect(() => {
    if (selectedWhitelabel) {
      dispatch(getAdvertisers(selectedWhitelabel as IWhitelabelControlOption));
      dispatch(getAgencies(selectedWhitelabel as IWhitelabelControlOption));
    }
  }, [selectedWhitelabel, dispatch]);

  useEffect(() => {
    if (selectedClient) {
      setOpenClientModal(true);
    }
  }, [selectedClient, setOpenClientModal]);

  useEffect(() => {
    dispatch(getClients());
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(getWhitelabels());
    dispatch(getAdvertiserCategories());

    return () => {
      dispatch(resetClientsPageState());
    };
  }, [dispatch]);

  const removeControlOption = useCallback(
    (advertiserToRemove: IControlOption) => {
      const newAdvertiserIds = selectedAdvertisersInClient.filter(({ value }) => value !== advertiserToRemove.value);
      const newAdvertisersInClient = selectedNewAdvertisersInClient.filter(
        ({ temporaryKey }) => temporaryKey !== advertiserToRemove.value,
      );

      setClientFormValue('newAdvertisers', newAdvertisersInClient);
      setClientFormValue('advertisers', newAdvertiserIds);
    },
    [setClientFormValue, selectedAdvertisersInClient, selectedNewAdvertisersInClient],
  );

  const handleCloseClientModal = useCallback(
    (refresh?: boolean) => {
      if (refresh) {
        dispatch(getClients());
      }
      dispatch(clearSelectedClient());
      setOpenClientModal(false);
      setAdvertiserModalMode(null);
    },
    [dispatch, setOpenClientModal, setAdvertiserModalMode],
  );

  const handleCloseAdvertiserModal = useCallback(
    (newAdvertiser: IClientAdvertiserPayload | null) => {
      if (newAdvertiser) {
        const controlOption = formatTemporaryControlOption(newAdvertiser);
        if (advertiserModalMode === AdvertiserModalMode.AGENCY) {
          setAgencyFormValue('newAdvertisers', [...selectedNewAdvertisersInAgency, newAdvertiser]);
          setAgencyFormValue('advertisers', [...selectedAdvertisersInAgency, controlOption]);
        }
        setClientFormValue('newAdvertisers', [...selectedNewAdvertisersInClient, newAdvertiser]);
        setClientFormValue('advertisers', [...selectedAdvertisersInClient, controlOption]);
      }
      setOpenAdvertiserModal(false);
      setAdvertiserModalMode(AdvertiserModalMode.CLIENT);
    },
    [
      dispatch,
      setOpenAdvertiserModal,
      selectedAdvertisersInClient,
      selectedNewAdvertisersInClient,
      selectedAdvertisersInAgency,
      selectedNewAdvertisersInAgency,
      setClientFormValue,
      advertiserModalMode,
      setAdvertiserModalMode,
    ],
  );

  const handleCloseAgencyModal = useCallback(
    (newAgency: IClientAgencyPayload | null) => {
      if (newAgency) {
        const controlOption = formatTemporaryControlOption(newAgency);
        setClientFormValue('newAgencies', [...selectedNewAgenciesInClient, newAgency]);
        setClientFormValue('agencies', [...selectedAgenciesInClient, controlOption]);
      }
      setOpenAgencyModal(false);
    },
    [dispatch, setOpenAgencyModal, selectedAgenciesInClient, selectedNewAgenciesInClient, setClientFormValue],
  );

  const handleOpenAdvertiserModalFromAgency = useCallback(() => {
    setAdvertiserModalMode(AdvertiserModalMode.AGENCY);
    setOpenAdvertiserModal(true);
  }, [setAdvertiserModalMode, setOpenAdvertiserModal]);

  const handleOpenAdvertiserModalFromClient = useCallback(() => {
    setAdvertiserModalMode(AdvertiserModalMode.CLIENT);
    setOpenAdvertiserModal(true);
  }, [setAdvertiserModalMode, setOpenAdvertiserModal]);

  return (
    <div className='w-full p-[30px]'>
      <Card className='h-full'>
        <ClientsGrid
          data={data}
          loading={isLoading}
          rowCount={total}
          handleCreationModalOpen={() => setOpenClientModal(true)}
        />
      </Card>
      <ClientModal
        form={clientForm}
        open={openClientModal}
        onClose={handleCloseClientModal}
        selectedClient={selectedClient}
        loading={isLoading}
        setLoading={setLoading}
        openAdvertiserModal={handleOpenAdvertiserModalFromClient}
        openAgencyModal={() => setOpenAgencyModal(true)}
      />
      <AddAdvertiserModal
        form={advertiserForm}
        open={openAdvertiserModal}
        onClose={handleCloseAdvertiserModal}
        clientId={selectedClient?.id}
        loading={isLoading}
      />
      <AddAgencyModal
        form={agencyForm}
        open={openAgencyModal}
        onClose={handleCloseAgencyModal}
        loading={isLoading}
        openAdvertiserModal={handleOpenAdvertiserModalFromAgency}
        removeControlOption={removeControlOption}
      />
    </div>
  );
};

export default Clients;
